import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import { get, isEmpty } from 'lodash';
import { RootState } from 'modules';
import queryString from 'query-string';
import { ProfileWrapper } from 'components/mentoring/styles';
import { showConfirm } from 'components/modal/showConfirm';
import { handleFieldError, moveToFirstError } from 'libs/api/errorHandle';
import {
  updateMentoringFeedback,
  getMentoringClass,
  mentoringFeedbackCreate,
  getMentoringFeedback,
} from 'libs/api/mentoring';
import { useDataApi } from 'libs/hooks';
import { TMentoringFeedback, TMentoringClass } from 'modules/mentoring';
import { RouteMatch } from 'modules/types';
import { CFeedbackScore } from 'constants/mentoring.const';
import { CUserType, CUserTypeColor } from 'constants/user.const';
import { EUserType, EOperation, ENotificationType } from 'enums/base.enum';
import { EFeedbackScore } from 'enums/mentoring.enum';
import { EMentoringField } from 'enums/mentoring.enum';
import { ERoute } from 'enums/route.enum';
import { Radio, Checkbox, Input, Button, Col, Row, Typography, Form, Spin, Tag } from 'antd';
import styled from 'styled-components';
import { PrimaryColor, FormWrapper, MenteeColor } from 'GlobalStyles';

const { Text, Title } = Typography;
const MentoringFeedback = () => {
  const mentoringClassId = String(get(queryString.parse(useLocation().search), 'id', ''));
  const match = useRouteMatch(`${ERoute.MentoringFeedback}/:operation/:record_id?`);
  let { operation = EOperation.View, record_id } = (match?.params as RouteMatch) || {};

  return (
    <>
      {operation === EOperation.Create && (
        <FeedbackCreate mentoringClassId={mentoringClassId} isEdit={false} />
      )}
      {operation === EOperation.Edit && (
        <FeedbackCreate recordId={record_id} mentoringClassId={mentoringClassId} isEdit={true} />
      )}
      {operation === EOperation.View && <FeedbackView recordId={record_id} />}
    </>
  );
};

function FeedbackScore({ name, disabled }: { name: string; disabled: boolean }) {
  return (
    <Form.Item
      name={name}
      className='form-item no-margin'
      rules={[{ required: true }]}
      style={{ marginBottom: '10px' }}
    >
      <Radio.Group className='col-20p' disabled={disabled}>
        <Radio.Button value={5}>{CFeedbackScore[EFeedbackScore.Excellent]}</Radio.Button>
        <Radio.Button value={4}>{CFeedbackScore[EFeedbackScore.Good]}</Radio.Button>
        <Radio.Button value={3}>{CFeedbackScore[EFeedbackScore.Average]}</Radio.Button>
        <Radio.Button value={2}>{CFeedbackScore[EFeedbackScore.NeedImprovement]}</Radio.Button>
        <Radio.Button value={1}>{CFeedbackScore[EFeedbackScore.Bad]}</Radio.Button>
        <Radio.Button value={0}>{CFeedbackScore[EFeedbackScore.NotApplicable]}</Radio.Button>
      </Radio.Group>
    </Form.Item>
  );
}

//피드백 작성창 - 초기 작성 및 수정 페이지
function FeedbackCreate({
  mentoringClassId,
  isEdit,
  recordId,
}: {
  mentoringClassId: string;
  isEdit: boolean;
  recordId?: number;
}) {
  const [{ data: mentoringClass, loading: mentoringClassLoading }] = useDataApi<TMentoringClass>(
    getMentoringClass.bind(null, Number(mentoringClassId)),
  );
  const [form] = Form.useForm();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(false);
  const history = useHistory();
  const [{ data, loading }] = useDataApi<TMentoringFeedback>(
    getMentoringFeedback.bind(null, Number(recordId)),
    {},
    isEdit,
  );
  const [isChecked, setIsChecked] = useState<boolean>();

  function onClickSubmit(isCompleted: boolean) {
    form
      .validateFields()
      .then((value) => {
        handleSubmit(value as TMentoringFeedback, isCompleted);
      })
      .catch((errorInfo) => {
        moveToFirstError(form, errorInfo.errorFields);
      });
  }

  async function handleSubmit(value: TMentoringFeedback, isCompleted: boolean) {
    try {
      setSubmitButtonDisabled(true);
      value.mentoring_class_id = Number(mentoringClassId);
      value.is_completed = isCompleted;
      value.case_tutorial = isChecked === undefined ? false : isChecked;
      if (get(mentoringClass, 'mentor.user.primary_field') === EMentoringField.Consulting) {
        value.overall_score === 0
          ? (value.overall_score = null)
          : (value.overall_score = value.overall_score);
        value.effectiveness_score === 0
          ? (value.effectiveness_score = null)
          : (value.effectiveness_score = value.effectiveness_score);
        value.problemsolving_score === 0
          ? (value.problemsolving_score = null)
          : (value.problemsolving_score = value.problemsolving_score);
        value.communication_score === 0
          ? (value.communication_score = null)
          : (value.communication_score = value.communication_score);
      } else if (get(mentoringClass, 'mentor.user.primary_field') === EMentoringField.Finance) {
        value.analytical_thinking_score === 0
          ? (value.analytical_thinking_score = null)
          : (value.analytical_thinking_score = value.analytical_thinking_score);
        value.motivation_to_develop_score === 0
          ? (value.motivation_to_develop_score = null)
          : (value.motivation_to_develop_score = value.motivation_to_develop_score);
        value.technical_skill_score === 0
          ? (value.technical_skill_score = null)
          : (value.technical_skill_score = value.technical_skill_score);
        value.finance_communication_score === 0
          ? (value.finance_communication_score = null)
          : (value.finance_communication_score = value.finance_communication_score);
      }

      isEdit === true
        ? await updateMentoringFeedback(Number(recordId), value)
        : await mentoringFeedbackCreate(value);

      const message = isCompleted === true ? '피드백이 제출 되었습니다.' : '피드백이 임시 저장 되었습니다.';

      history.push({
        pathname: ERoute.MentoringMyPage,
        state: {
          notification: {
            type: ENotificationType.Success,
            message: message,
          },
        },
      });
    } catch (e) {
      handleFieldError(e, form);
      throw e;
    } finally {
      setSubmitButtonDisabled(false);
    }
  }

  useEffect(() => {
    //isEmpty(data) == false => 초기작성페이지가 아닌 임시저장후 수정페이지
    if (isEmpty(data) == false) {
      form.setFieldsValue({
        //consulting
        overall: get(data, 'overall'),
        effectiveness: get(data, 'effectiveness'),
        problemsolving: get(data, 'problemsolving'),
        communication: get(data, 'communication'),
        overall_score: get(data, 'overall_score'),
        effectiveness_score: get(data, 'effectiveness_score'),
        problemsolving_score: get(data, 'problemsolving_score'),
        communication_score: get(data, 'communication_score'),
        //finance
        analytical_thinking: get(data, 'analytical_thinking'),
        motivation_to_develop: get(data, 'motivation_to_develop'),
        technical_skill: get(data, 'technical_skill'),
        finance_communication: get(data, 'finance_communication'),
        analytical_thinking_score: get(data, 'analytical_thinking_score'),
        motivation_to_develop_score: get(data, 'motivation_to_develop_score'),
        technical_skill_score: get(data, 'technical_skill_score'),
        finance_communication_score: get(data, 'finance_communication_score'),
      });
    }
  }, [data]);

  function onCheck(e: any) {
    setIsChecked(e.target.checked);
  }

  useEffect(() => {
    //체크박스 있는 컨설팅 멘토링에서만
    //체크 선택시 점수표 비활성화 및 피드백 섹션 자동 완성
    if (isChecked) {
      form.setFieldsValue({
        //스코어 비활성화 && 멘트 비활성화
        //멘트 세팅은 됐는데 비활성화는 어떻게??
        overall: 'Case tutorial은 mock interview 피드백이 제공되지 않습니다.',
        effectiveness: 'Case tutorial은 mock interview 피드백이 제공되지 않습니다.',
        problemsolving: 'Case tutorial은 mock interview 피드백이 제공되지 않습니다.',
        communication: 'Case tutorial은 mock interview 피드백이 제공되지 않습니다.',
        overall_score: 0,
        effectiveness_score: 0,
        problemsolving_score: 0,
        communication_score: 0,
      });
    } else if (isChecked === false) {
      form.setFieldsValue({
        overall: get(data, 'overall'),
        effectiveness: get(data, 'effectiveness'),
        problemsolving: get(data, 'problemsolving'),
        communication: get(data, 'communication'),
        overall_score: 5,
        effectiveness_score: 5,
        problemsolving_score: 5,
        communication_score: 5,
      });
    }
  }, [isChecked]);

  return (
    <Spin tip='loading...' spinning={loading}>
      <FeedbackHeader mentoringClass={mentoringClass} title='피드백을 작성해주세요.' />
      <FormWrapper style={{ maxWidth: '800px' }}>
        {/* 멘토의 멘토링 필드가 컨설팅일 때만 나타날 수 있도록 */}
        {mentoringClass?.mentor.mentoring_field === EMentoringField.Consulting && (
          <Checkbox onChange={onCheck} checked={isChecked} style={{ marginBottom: '10px' }}>
            Case tutorial 멘토링인 경우 체크해주세요 - 체크 시 피드백을 생략합니다.
          </Checkbox>
        )}
        <Form
          form={form}
          layout='vertical'
          className='form-normal'
          initialValues={{
            overall_score: 5,
            effectiveness_score: 5,
            problemsolving_score: 5,
            communication_score: 5,
            analytical_thinking_score: 5,
            motivation_to_develop_score: 5,
            technical_skill_score: 5,
            finance_communication_score: 5,
          }}
        >
          <FeedbackForm
            operation={EOperation.Create}
            isChecked={isChecked}
            mentorPrimaryField={get(mentoringClass, 'mentor.user.primary_field')}
          />
          <Form.Item shouldUpdate={true}>
            {() => (
              <Row justify='end'>
                <Button
                  size='large'
                  htmlType='submit'
                  style={{ marginRight: '5px' }}
                  loading={submitButtonDisabled}
                  onClick={onClickSubmit.bind(null, false)}
                  disabled={isChecked}
                >
                  임시 저장
                </Button>
                <Button
                  type='primary'
                  size='large'
                  htmlType='submit'
                  loading={submitButtonDisabled}
                  onClick={() => {
                    showConfirm(
                      onClickSubmit.bind(null, true),
                      <>
                        <div style={{ fontSize: '14px' }}>피드백을 제출하시겠습니까?</div>
                        <div style={{ fontSize: '12px', marginTop: '10px' }}>
                          제출한 뒤에는 수정이 불가능합니다.
                        </div>
                      </>,
                    );
                  }}
                >
                  제출
                </Button>
              </Row>
            )}
          </Form.Item>
        </Form>
      </FormWrapper>
    </Spin>
  );
}
// 피드백 보기 창
function FeedbackView({ recordId }: { recordId: number }) {
  const [{ data, loading }] = useDataApi<TMentoringFeedback>(
    getMentoringFeedback.bind(null, Number(recordId)),
  );
  const [form] = Form.useForm();
  const FeedbackViewWrapper = styled.div`
    .ant-input[disabled],
    .ant-radio-button-wrapper-disabled {
      cursor: default;
      color: #000;
    }
    .ant-radio-button-wrapper-checked {
      border: 1px solid ${PrimaryColor} !important;
      background: #fff !important;
      color: ${PrimaryColor} !important;
    }
    .ant-radio-button-wrapper:not(:first-child)::before {
      background-color: transparent;
    }
  `;

  //멘티에게 보여지 피드백/멘토가 작성 완료하고 확인하는 피드백 페이지는 데이터 받아서 setFieldsValue
  useEffect(() => {
    form.setFieldsValue({
      //consulting
      overall: get(data, 'overall'),
      effectiveness: get(data, 'effectiveness'),
      problemsolving: get(data, 'problemsolving'),
      communication: get(data, 'communication'),
      overall_score: get(data, 'overall_score'),
      effectiveness_score: get(data, 'effectiveness_score'),
      problemsolving_score: get(data, 'problemsolving_score'),
      communication_score: get(data, 'communication_score'),
      //finance
      analytical_thinking: get(data, 'analytical_thinking'),
      motivation_to_develop: get(data, 'motivation_to_develop'),
      technical_skill: get(data, 'technical_skill'),
      finance_communication: get(data, 'finance_communication'),
      analytical_thinking_score: get(data, 'analytical_thinking_score'),
      motivation_to_develop_score: get(data, 'motivation_to_develop_score'),
      technical_skill_score: get(data, 'technical_skill_score'),
      finance_communication_score: get(data, 'finance_communication_score'),
    });
  }, [data]);

  return (
    <FeedbackViewWrapper>
      <Spin tip='loading...' spinning={loading}>
        <FeedbackHeader mentoringClass={get(data, 'mentoring_class')} title='수업 피드백' />
        <FormWrapper style={{ maxWidth: '800px' }}>
          <Form form={form} layout='vertical' className='form-normal'>
            <FeedbackForm
              operation={EOperation.View}
              mentorPrimaryField={get(data, 'mentoring_class.mentor.mentoring_field')}
            />
          </Form>
        </FormWrapper>
      </Spin>
    </FeedbackViewWrapper>
  );
}

//피드백 form
function FeedbackForm({
  operation,
  isChecked,
  mentorPrimaryField,
}: {
  operation: EOperation;
  isChecked?: boolean;
  mentorPrimaryField?: string;
}) {
  const currentUser = useSelector((state: RootState) => state.user.current_user, shallowEqual);
  const [disabled, setDisabled] = useState<boolean>(operation === EOperation.View);
  useEffect(() => {
    if (operation === EOperation.View) {
      //피드백 보기 페이지일 때
      setDisabled(true);
    } else if (operation === EOperation.Create && isChecked === true) {
      //피드백 작성 && Case tutorial 멘토링 체크되었을 때
      setDisabled(true);
    } else {
      //위의 상황이 아니면 스코어 버튼 활성화 EOperation.Edit 이나 EOperation.Create 일 때
      setDisabled(false);
    }
  }, [isChecked]);

  return (
    <>
      {mentorPrimaryField === EMentoringField.Consulting && (
        <>
          <Title level={3}>Overall & Recommendation</Title>
          <FeedbackScore name='overall_score' disabled={disabled} />
          <Form.Item name='overall' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='멘티에 대한 평가 결과 및 멘토링 이후 연습 방식에 대해 종합적인 평을 남겨주세요.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
          <Title level={3}>Impact</Title>
          <FeedbackScore name='effectiveness_score' disabled={disabled} />
          <Form.Item name='effectiveness' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='Fit Interview를 중심으로 Consulting Fit에 어울리게 전달하고 있는지를 평가해주세요.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
          <Title level={3}>Problem Solving</Title>
          <FeedbackScore name='problemsolving_score' disabled={disabled} />
          <Form.Item name='problemsolving' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='Case Interview를 중심으로 구조화, 비즈니스 센스 등을 평가해주세요.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
          <Title level={3}>Communication</Title>
          <FeedbackScore name='communication_score' disabled={disabled} />
          <Form.Item name='communication' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='Speaking & Listening에 걸친 전반적인 커뮤니케이션 역량에 대한 평가 부탁 드립니다.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
        </>
      )}
      {mentorPrimaryField === EMentoringField.Finance && (
        <>
          <Title level={3}>Analytical thinking</Title>
          <FeedbackScore name='analytical_thinking_score' disabled={disabled} />
          <Form.Item name='analytical_thinking' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='분석적이고 논리적인 사고력, 문제해결능력 및 비즈니스 센스에 대해서 평가해주세요.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
          <Title level={3}>Motivation to develop</Title>
          <FeedbackScore name='motivation_to_develop_score' disabled={disabled} />
          <Form.Item name='motivation_to_develop' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='피드백에 대한 반응(조언을 수용하는 자세), 배우고자 하는 열정 등 전반적인 태도에 대해서 평가해주세요.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
          <Title level={3}>Technical skill</Title>
          <FeedbackScore name='technical_skill_score' disabled={disabled} />
          <Form.Item name='technical_skill' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='기본적인 재무 개념과 Financial modelling에 대한 이해도, 전반적인 경제 상황에 대한 해석 능력 등을 평가해주세요.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
          <Title level={3}>Communication</Title>
          <FeedbackScore name='finance_communication_score' disabled={disabled} />
          <Form.Item name='finance_communication' className='form-item' rules={[{ required: true }]}>
            <Input.TextArea
              className='form_textarea'
              placeholder='Speaking & Listening에 걸친 전반적인 커뮤니케이션 역량에 대한 평가 부탁 드립니다.'
              autoSize={{ minRows: 8 }}
              disabled={disabled}
            />
          </Form.Item>
        </>
      )}
    </>
  );
}

function FeedbackHeader({ mentoringClass, title }: { mentoringClass: TMentoringClass; title: string }) {
  return (
    <div style={{ textAlign: 'center', paddingTop: '40px' }}>
      <Title level={2}>{title}</Title>
      <ProfileWrapper>
        <Row justify='center' gutter={[8, 8]}>
          <Col flex='120px'>
            <img
              alt='profile_image'
              className='no-hover size-sm'
              src={get(mentoringClass, 'mentor.user.profile_image.absolute_url')}
            />
            <Tag className='user-type-tag' color={CUserTypeColor[EUserType.Mentor]}>
              {CUserType[EUserType.Mentor]}
            </Tag>
            <div className='name' style={{ marginTop: '-10px' }}>
              {get(mentoringClass, 'mentor.user.fullname')}
            </div>
          </Col>
          <Col flex='120px'>
            <img
              alt='profile_image'
              className='no-hover size-sm'
              src={get(mentoringClass, 'mentee.user.profile_image.absolute_url')}
            />
            <Tag className='user-type-tag' color={MenteeColor}>
              {CUserType[EUserType.Mentee]}
            </Tag>
            <div className='name' style={{ marginTop: '-10px' }}>
              {get(mentoringClass, 'mentee.user.fullname')}
            </div>
          </Col>
        </Row>
      </ProfileWrapper>
    </div>
  );
}

export default MentoringFeedback;
